import {
  GRID_ROOT_GROUP_ID,
  gridRowNodeSelector,
  type GridAggregationFunction,
  type GridApiPremium,
  type GridGroupNode,
  type GridTreeNode,
} from '@mui/x-data-grid-premium';
import type { RefObject } from 'react';
import type { HistoricalComparisonAmounts } from '@/utils/historicalBalance';
import {
  groupPathKey,
  isSplitGroup,
  type GroupTotals,
  type GroupTotalsByPath,
} from './balanceSheetHistoricalGroups';

export const SECTION_SUBTOTAL = 'sectionSubtotal';

export type GroupTotalsLookup = (
  node: GridGroupNode,
) => GroupTotals | undefined;

type GridApiRef = RefObject<GridApiPremium | null>;

const SUBTOTAL_FIELDS = new Set<string>([
  'OldDollarBalance',
  'DollarBalance',
  'DollarChange',
  'PercentChange',
]);

const isSubtotalField = (
  field: string,
): field is keyof HistoricalComparisonAmounts => SUBTOTAL_FIELDS.has(field);

const groupKeyLabel = (key: GridGroupNode['groupingKey']): string => {
  if (key == null) return '';
  return typeof key === 'string' ? key : String(key);
};

export const createGroupTotalsLookup =
  (totalsByPath: GroupTotalsByPath, apiRef: GridApiRef): GroupTotalsLookup =>
  (node) => {
    const path: string[] = [];
    let current: GridTreeNode | undefined = node;
    while (current?.type === 'group' && current.id !== GRID_ROOT_GROUP_ID) {
      path.unshift(groupKeyLabel(current.groupingKey));
      current =
        current.parent == null
          ? undefined
          : gridRowNodeSelector(apiRef, current.parent);
    }
    return totalsByPath.get(groupPathKey(path));
  };

export const sectionSubtotal = (
  totals: GroupTotals | undefined,
  field: keyof HistoricalComparisonAmounts,
): number | null | undefined =>
  totals && !isSplitGroup(totals) ? totals[field] : undefined;

/** Feeds the on-screen subtotals to the grid, so the export carries them. */
export const createSectionSubtotalAggregation = (
  getGroupTotals: GroupTotalsLookup,
  apiRef: GridApiRef,
): GridAggregationFunction<number | null, number | null> => ({
  label: '',
  hasCellUnit: true,
  apply: ({ groupId, field }) => {
    const node = gridRowNodeSelector(apiRef, groupId);
    if (node.type !== 'group' || !isSubtotalField(field)) return null;
    return sectionSubtotal(getGroupTotals(node), field) ?? null;
  },
});
